import { Component, inject, OnInit, ViewChild, computed, effect } from '@angular/core';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { DynamicForm, SelectOption } from '../../../../components/form-builder/form-builder.types';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { UserService } from '../user.service';
import { getUpdateUserFormObject } from '../utils/user-form-object';
import { UserRepository } from '@/store/user-state/user-state.repository';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { SpinnerComponent } from '../../../../components/shared/spinner/spinner.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DataTableBuilderComponent } from '../../../../components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '../../../../components/data-table-builder/data-table.types';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogComponent } from '../../../../components/dialog/dialog.component';
import { DialogObject } from '../../../../components/dialog/dialog.types';
import { FieldVariant } from '../../../../components/form-builder/form-builder.types';
import { MultiSelectModule } from 'primeng/multiselect';
import { ContractingAuthorityService } from '../../contracting-authority/contracting-authority.service';
import { CommitteeService } from '../../committee/committee.service';
import { ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { of, BehaviorSubject } from 'rxjs';
import { UserString, UserStringDto } from '@/types';
import { UserAC, UserCommiittee, UserDto, UserRole } from '@/types/user.types';
import { AuthorizationService } from '@/services/authorization.service';
import { getUserCommitteeDataTableStructure } from './utils/user-committee-data-table-object';
import { getUserACDataTableStructure } from './utils/user-ac-data-table-object';
import { getUserRoleDataTableStructure } from './utils/user-role-data-table-object';

interface RoleOption {
  name: string;
  label: string;
  code: string;
}

@Component({
  selector: 'sm-update-user',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    ButtonModule,
    TabsModule,
    CommonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    DialogModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerModule,
    TabsModule,
    SpinnerComponent,
    TranslateModule,
    DataTableBuilderComponent,
    DialogComponent,
    MultiSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: './update-user.component.html',
  styleUrl: './update-user.component.scss',
  standalone: true,
})
export class UpdateUserComponent implements OnInit {
  userService = inject(UserService);
  userRepository = inject(UserRepository);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);
  router = inject(Router);
  domSanitizer = inject(DomSanitizer);
  contractingAuthorityService = inject(ContractingAuthorityService);
  committeeService = inject(CommitteeService);
  authService = inject(AuthorizationService);

  isAdmin = this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN]);

  @ViewChild(FormBuilderComponent) formBuilderComponent!: FormBuilderComponent;

  queryParamMapSignal = toSignal(this.activatedRoute.queryParamMap, {
    initialValue: new Map() as unknown as ParamMap,
  });

  isInspectMode = computed(() => {
    const paramMap = this.queryParamMapSignal();
    return paramMap.get('inspect') === 'true';
  });

  constructor() {
    effect(() => {
      this.userRepository.setNested('isDisabled', {
        username: this.isInspectMode(),
        firstName: this.isInspectMode(),
        lastName: this.isInspectMode(),
        email: this.isInspectMode(),
        roles: this.isInspectMode(),
        acs: this.isInspectMode(),
        coms: this.isInspectMode(),
        address: this.isInspectMode(),
        mobile: this.isInspectMode(),
        fixe: this.isInspectMode(),
        password: this.isInspectMode(),
        confirmPassword: this.isInspectMode(),
        acPilote: this.isInspectMode(),
      });
      this.formObject = getUpdateUserFormObject({
        store: this.userRepository,
        rolesOptions: this.rolesOptions,
        acsOptions: this.userAcs.value.map(ac => ({ name: ac.nom, code: ac.id })),
        comsOptions: this.userComs.map(com => ({ name: com.nom, code: com.id })),
      });
    });
  }

  breadcrumb = [
    {
      label: 'settings.user.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.user.breadcrumb.user',
      routerLink: ['/settings', 'user'],
    },
    {
      label: this.isInspectMode() ? 'settings.user.breadcrumb.consult' : 'settings.user.breadcrumb.update',
      routerLink: ['/settings', 'user', 'update'],
    },
  ];

  get pageTitle() {
    return this.isInspectMode() ? 'settings.user.consult.title' : 'settings.user.update.title';
  }
  get pageDescription() {
    return this.isInspectMode() ? 'settings.user.consult.description' : 'settings.user.update.description';
  }

  formDateLoading = false;
  loading = false;

  id = '';
  rolesOptions: RoleOption[] = [];
  selectedTab = '0';
  userRoles: string[] = [];
  userAcs: BehaviorSubject<UserAC[]> = new BehaviorSubject<UserAC[]>([]);
  userComs: UserCommiittee[] = [];
  userRolesTableData: any[] = [];

  rolesTableObject = getUserRoleDataTableStructure({
    translate: this.translate,
    userRoles: this.userRoles,
    onDetach: () => this.onDetach,
  });

  acRowActions: DataTableRowActions = {
    additionalActions: {
      Actions: [
        {
          label: this.translate.instant('settings.userInfo.tabs.deleteAc'),
          icon: 'pi pi-trash',
          action: (row: any) => {
            if (row && row.id) {
              this.onDetach(row.id, 'ac');
            }
          },
        },
      ],
    },
  };

  acsTableObject: DynamicDataTable = getUserACDataTableStructure({
    isInspectMode: this.isInspectMode,
    onAcPiloteChange: () => this.onAcPiloteChange,
    userAcs: this.userAcs,
    rowActions: this.isInspectMode() ? undefined : this.acRowActions,
  });

  comRowActions: DataTableRowActions = {
    additionalActions: {
      Actions: [
        {
          label: this.translate.instant('settings.userInfo.tabs.deleteAc'),
          icon: 'pi pi-trash',
          action: (row: any) => {
            if (row && row.id) {
              this.onDetach(row.id, 'com');
            }
          },
        },
      ],
    },
  };

  comsTableObject: DynamicDataTable = getUserCommitteeDataTableStructure({
    rowActions: this.isInspectMode() || this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN]) ? undefined : this.comRowActions,
  });

  userDialog?: DialogObject;
  dialogVisible = false;
  editDialogVisible = false;
  editDialogFormObject: DynamicForm = {
    title: '',
    description: '',
    isHeaderHidden: false,
    grids: [],
    orientation: 'vertical',
  };
  editDialog?: DialogObject;
  currentEditType: 'role' | 'ac' | 'com' = 'role';

  // Initialize formObject with a minimal structure at declaration
  formObject: DynamicForm = {
    title: '',
    description: '',
    class: '',
    grids: [],
    orientation: 'vertical',
  };

  // Add form validity observable
  formValid$ = of(true); // Always return true since we removed validation

  private initialFormValues: any = null;
  hasFormChanges = false;

  // Add BehaviorSubjects for multiselect values
  private rolesValue$ = new BehaviorSubject<RoleOption[]>([]);
  private acsValue$ = new BehaviorSubject<SelectOption[]>([]);
  private comsValue$ = new BehaviorSubject<SelectOption[]>([]);

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.params['id'];
    this.loadUserData();
    this.loadRolesOptions();
  }

  loadUserData() {
    this.userService.getUserById(this.id).subscribe({
      next: (user: UserString) => {
        this.userRepository.set('response', user);

        // Initialize DTO with all required fields
        const dto: UserStringDto = {
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          password: '',
          address: user.address || '',
          mobile: user.mobile || '',
          fixe: user.fixe || '',
          comIds: user.coms?.map(com => com.id) || [],
          acIds: user.acs?.map(ac => ac.id) || [],
          acPiloteId: user.acs?.find((ac: UserAC) => ac.acPilote === true)?.id || 0,
          roles: user.roles || [],
          enable: user.enabled ?? true,
        };
        this.userRepository.set('dto', dto);

        // Update roles
        this.userRoles = user.roles || [];

        // Map the roles to full option objects and set them in the repository
        const selectedRoles = this.userRoles
          .map(roleCode => {
            const found = this.rolesOptions.find(opt => opt.code === roleCode || opt.label === roleCode);
            return found ? { name: found.name, code: found.code, label: found.label } : undefined;
          })
          .filter(Boolean) as RoleOption[];

        this.userRepository.set('UserEnabledSelected', selectedRoles);
        this.rolesValue$.next(selectedRoles);

        // Update ACs
        this.userAcs.next(user.acs || []);
        const selectedAcs = this.userAcs.value.map(ac => ({ name: ac.nom, code: ac.id }));
        this.acsValue$.next(selectedAcs);

        // Update Coms
        this.userComs = user.coms || [];
        const selectedComs = this.userComs.map(com => ({ name: com.nom, code: com.id }));
        this.comsValue$.next(selectedComs);

        this.updateRolesTableData();
        this.initialFormValues = JSON.parse(JSON.stringify(dto));
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.loaded'),
        });
      },
    });
  }

  loadRolesOptions() {
    this.userService.getAllRoles().subscribe({
      next: roles => {
        if (!Array.isArray(roles)) {
          return;
        }
        // Map roles to { name, label, code } with name/code as backend name
        this.rolesOptions = roles.map((role: any) => ({
          name: role.name,
          label: role.label || role.name,
          code: role.name,
        }));
        this.formDateLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.loadingRoles'),
        });
        this.formDateLoading = false;
      },
    });
  }

  private updateRolesTableData() {
    this.userRolesTableData = Array.isArray(this.userRoles)
      ? this.userRoles.map(roleName => {
          const roleOption = this.rolesOptions.find(opt => opt.name === roleName);
          return {
            role: roleOption ? roleOption.label : roleName,
            name: roleName,
          };
        })
      : [];
  }

  onTabChange(event: any) {
    this.selectedTab = event;
  }

  updateUser() {
    // Mark form as submitted to trigger error display
    this.userRepository.set('formSubmitted', true);
    // Mark all relevant fields as touched on form submission
    this.userRepository.set('usernameTouched', true);
    this.userRepository.set('firstNameTouched', true);
    this.userRepository.set('lastNameTouched', true);
    this.userRepository.set('emailTouched', true);
    this.userRepository.set('passwordTouched', true);
    this.userRepository.set('confirmPasswordTouched', true);
    this.userRepository.set('rolesTouched', true);
    this.userRepository.set('acsTouched', true);
    this.userRepository.set('acPiloteTouched', true);
    this.userRepository.set('comsTouched', true);
    this.userRepository.set('addressTouched', true);
    this.userRepository.set('mobileTouched', true);
    this.userRepository.set('fixeTouched', true);

    // Only open dialog if form is valid
    this.formValid$.subscribe((valid: boolean) => {
      if (valid) {
        this.userDialog = {
          title: this.translate.instant('settings.user.add.confirmTitle'),
          description: this.translate.instant('settings.user.add.confirmDescription'),
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onHide: () => {},
          actions: [
            {
              label: this.translate.instant('common.confirm'),
              variant: 'success',
              onClick: () => {
                this.dialogVisible = false;
                this.performUpdateUser();
              },
            },
            {
              label: this.translate.instant('common.cancel'),
              variant: 'secondary',
              onClick: () => {
                this.dialogVisible = false;
              },
            },
          ],
        };
        this.dialogVisible = true;
      }
    });
  }

  performUpdateUser() {
    this.loading = true;
    const dto = this.userRepository.get('dto') as any;
    // Get selected values from repository (if set by dialog), else fallback to current state
    const selectedRoles = this.userRepository.get<SelectOption[]>('UserEnabledSelected') || this.userRoles.map(role => ({ code: role }));
    const selectedAcs = this.userRepository.get<SelectOption[]>('selectedAcs') || this.userAcs.value.map(ac => ({ code: ac.id }));
    const selectedComs = this.userRepository.get<SelectOption[]>('selectedComs') || this.userComs.map(com => ({ code: com.id }));
    const roleCodes = selectedRoles.map(role => String(role.code)).filter(code => !!code);
    const acIds = selectedAcs.map(ac => Number(ac.code));
    const comIds = selectedComs.map(com => Number(com.code));
    // Set acPiloteId logic
    let acPiloteId = dto.acPiloteId ? Number(dto.acPiloteId) : 0;
    if ((!acPiloteId || acPiloteId === 0) && acIds.length > 0) {
      acPiloteId = acIds[0];
    }
    const requestBody = {
      username: dto.username,
      firstName: dto.firstName,
      lastName: dto.lastName,
      email: dto.email,
      address: dto.address || '',
      mobile: dto.mobile || '',
      fixe: dto.fixe || '',
      password: dto.password || '',
      comIds: comIds,
      acIds: acIds,
      acPiloteId: acPiloteId,
      roles: roleCodes,
      enable: dto.enable ?? true,
    };
    this.userService.updateUser(this.id, requestBody).subscribe({
      next: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.updated'),
        });
        this.loadUserData();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.updated'),
        });
      },
    });
  }

  openEditRolesDialog() {
    this.currentEditType = 'role';

    // First, ensure we have the roles options loaded
    if (!this.rolesOptions.length) {
      this.loadRolesOptions();
    }

    // Map user's current roles to full option objects
    const selectedRoles = this.userRoles
      .map(roleCode => {
        const found = this.rolesOptions.find(opt => opt.code === roleCode || opt.label === roleCode);
        return found ? { name: found.name, code: found.code, label: found.label } : undefined;
      })
      .filter(Boolean) as SelectOption[];

    // Set the selected roles in the repository
    this.userRepository.set('UserEnabledSelected', selectedRoles);

    // Create the form object with the multiselect
    this.editDialogFormObject = {
      title: this.translate.instant('settings.user.edit.roles.title'),
      description: '',
      grids: [
        {
          title: '',
          isHeaderHidden: true,
          gridItems: [
            {
              fields: [
                {
                  label: 'settings.user.edit.roles.label',
                  variant: FieldVariant.MULTISELECT,
                  description: 'settings.user.edit.roles.description',
                  isRequired: true,
                  props: {
                    placeholder: 'settings.user.edit.roles.placeholder',
                    options: this.rolesOptions,
                    value: this.userRepository.getObservable('UserEnabledSelected'),
                    onSelectChange: (value: SelectOption[]) => {
                      const newValue = value || [];
                      this.userRepository.set('UserEnabledSelected', newValue);
                    },
                    isMulti: true,
                    optionLabel: 'label',
                  },
                },
              ],
            },
          ],
        },
      ],
    };

    // Create the dialog
    this.editDialog = {
      title: this.translate.instant('settings.user.edit.roles.title'),
      description: '',
      onHide: () => {
        this.editDialogVisible = false;
      },
      actions: [
        {
          label: this.translate.instant('common.confirm'),
          variant: 'success',
          onClick: () => {
            this.editDialogVisible = false;
            this.performUpdateRoles();
          },
        },
        {
          label: this.translate.instant('common.cancel'),
          variant: 'secondary',
          onClick: () => {
            this.editDialogVisible = false;
          },
        },
      ],
      component: {
        outlet: FormBuilderComponent,
        props: {
          formObject: this.editDialogFormObject,
        },
      },
    };

    this.editDialogVisible = true;
  }

  performUpdateRoles() {
    const selectedRoles = this.userRepository.get<SelectOption[]>('UserEnabledSelected') || [];
    // Use .code as backend name for each selected role
    const roles = selectedRoles.map(role => String(role.code)).filter(code => !!code);
    const userDto = this.userRepository.get('dto') as any;

    // Always persist current acIds, comIds, and acPiloteId
    const acIds = Array.isArray(userDto.acIds) ? [...userDto.acIds] : [];
    const comIds = Array.isArray(userDto.comIds) ? [...userDto.comIds] : [];
    const acPiloteId = typeof userDto.acPiloteId !== 'undefined' && userDto.acPiloteId !== null ? userDto.acPiloteId : null;

    // Validation: roles must not be empty and must all be valid names
    if (!roles.length || roles.some(r => !this.rolesOptions.find(opt => opt.name === r))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'La liste des rôles est invalide ou vide. Veuillez sélectionner au moins un rôle valide.',
      });
      return;
    }

    // Always send all arrays in the request
    const requestBody: any = {
      username: userDto.username,
      firstName: userDto.firstName,
      lastName: userDto.lastName,
      email: userDto.email,
      address: userDto.address || '',
      mobile: userDto.mobile || '',
      fixe: userDto.fixe || '',
      password: userDto.password || '',
      comIds: comIds,
      acIds: acIds,
      roles: roles,
      enable: userDto.enable ?? true,
    };
    if (typeof acPiloteId === 'number' && acPiloteId !== 0) {
      requestBody.acPiloteId = acPiloteId;
    }

    this.userService.updateUser(this.id, requestBody).subscribe({
      next: () => {
        this.userRoles = roles;
        this.updateRolesTableData();
        const updatedUser = { ...userDto, roles: roles };
        this.userRepository.set('dto', updatedUser);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.updated'),
        });
        this.loadUserData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.updated'),
        });
      },
    });
  }

  openEditAcsDialog() {
    this.currentEditType = 'ac';
    // Initialize the selected ACs
    const selectedAcs = this.userAcs.value.map(ac => ({ name: ac.nom, code: ac.id }));
    this.userRepository.set('selectedAcs', selectedAcs);

    // Subscribe to get all ACs
    this.contractingAuthorityService.getContractingAuthoritiesListUm().subscribe(response => {
      const acOptions = response.map(ac => ({ name: ac.nom, code: ac.id }));

      this.editDialogFormObject = {
        title: this.translate.instant('settings.user.edit.acs.title'),
        description: '',
        grids: [
          {
            title: '',
            isHeaderHidden: true,
            gridItems: [
              {
                fields: [
                  {
                    label: 'settings.user.edit.acs.label',
                    variant: FieldVariant.MULTISELECT,
                    description: 'settings.user.edit.acs.description',
                    isRequired: false,
                    props: {
                      placeholder: 'settings.user.edit.acs.placeholder',
                      options: acOptions,
                      value: this.userRepository.getObservable('selectedAcs'),
                      onSelectChange: (value: SelectOption[]) => {
                        this.userRepository.set('selectedAcs', value);
                      },
                      isMulti: true,
                    },
                  },
                ],
              },
            ],
          },
        ],
      };

      this.editDialog = {
        title: this.translate.instant('settings.user.edit.acs.title'),
        description: '',
        onHide: () => {
          this.editDialogVisible = false;
        },
        actions: [
          {
            label: this.translate.instant('common.confirm'),
            variant: 'success',
            onClick: () => {
              this.editDialogVisible = false;
              this.performUpdateAcs();
            },
          },
          {
            label: this.translate.instant('common.cancel'),
            variant: 'secondary',
            onClick: () => {
              this.editDialogVisible = false;
            },
          },
        ],
        component: {
          outlet: FormBuilderComponent,
          props: {
            formObject: this.editDialogFormObject,
          },
        },
      };
      this.editDialogVisible = true;
    });
  }

  performUpdateAcs() {
    const selectedAcs = this.userRepository.get<SelectOption[]>('selectedAcs') || [];
    const acIds = selectedAcs.map(ac => Number(ac.code));
    const userDto = this.userRepository.get('dto') as any;
    // Persist current roles as backend names
    const selectedRoles = this.userRepository.get<SelectOption[]>('UserEnabledSelected') || this.userRoles.map(role => ({ code: role }));
    const roles = selectedRoles.map(role => String(role.code)).filter(code => !!code);
    // Persist current coms as ids, fallback to userDto.comIds if selectedComs is empty
    const selectedComs = this.userRepository.get<SelectOption[]>('selectedComs');
    let comIds: number[];
    if (selectedComs && selectedComs.length > 0) {
      comIds = selectedComs.map(com => Number(com.code));
    } else if (Array.isArray(userDto.comIds)) {
      comIds = [...userDto.comIds];
    } else {
      comIds = this.userComs.map(com => com.id);
    }
    // Only include acPiloteId if valid
    const acPiloteId = typeof userDto.acPiloteId === 'number' && userDto.acPiloteId !== 0 ? userDto.acPiloteId : undefined;
    const requestBody: any = {
      username: userDto.username,
      firstName: userDto.firstName,
      lastName: userDto.lastName,
      email: userDto.email,
      address: userDto.address || '',
      mobile: userDto.mobile || '',
      fixe: userDto.fixe || '',
      password: userDto.password || '',
      comIds: comIds,
      acIds: acIds,
      acPiloteId: acPiloteId,
      roles: roles,
      enable: userDto.enable ?? true,
    };
    if (acPiloteId !== undefined) {
      requestBody.acPiloteId = acPiloteId;
    }
    this.userService.updateUser(this.id, requestBody).subscribe({
      next: () => {
        const updatedUser = { ...userDto, acIds: acIds };
        this.userRepository.set('dto', updatedUser);
        this.contractingAuthorityService.getContractingAuthorities().subscribe(response => {
          this.userAcs.next(
            response.content
              .filter(ac => acIds.includes(ac.id))
              .map(ac => ({
                id: ac.id,
                nom: ac.nom,
                createdBy: ac.metadata.createdBy,
                createdAt: ac.metadata.createdAt,
                ancrage: ac.ancrage ?? null,
                contact: ac.contact?.address ?? null,
                acPilote: acPiloteId === ac.id,
              }))
          );
        });
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.updated'),
        });
        this.loadUserData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.updated'),
        });
      },
    });
  }

  openEditComsDialog() {
    this.currentEditType = 'com';
    // Initialize the selected COMs
    const selectedComs = this.userComs.map(com => ({ name: com.nom, code: com.id }));
    this.userRepository.set('selectedComs', selectedComs);

    // Subscribe to get all COMs
    this.committeeService.getCommitteesList().subscribe(response => {
      const comOptions = response.map(com => ({ name: com.nom, code: com.id }));

      this.editDialogFormObject = {
        title: this.translate.instant('settings.user.edit.coms.title'),
        description: '',
        grids: [
          {
            title: '',
            isHeaderHidden: true,
            gridItems: [
              {
                fields: [
                  {
                    label: 'settings.user.edit.coms.label',
                    variant: FieldVariant.MULTISELECT,
                    description: 'settings.user.edit.coms.description',
                    isRequired: false,
                    props: {
                      placeholder: 'settings.user.edit.coms.placeholder',
                      options: comOptions,
                      value: this.userRepository.getObservable('selectedComs'),
                      onSelectChange: (value: SelectOption[]) => {
                        this.userRepository.set('selectedComs', value);
                      },
                      isMulti: true,
                    },
                  },
                ],
              },
            ],
          },
        ],
      };

      this.editDialog = {
        title: this.translate.instant('settings.user.edit.coms.title'),
        description: '',
        onHide: () => {
          this.editDialogVisible = false;
        },
        actions: [
          {
            label: this.translate.instant('common.confirm'),
            variant: 'success',
            onClick: () => {
              this.editDialogVisible = false;
              this.performUpdateComs();
            },
          },
          {
            label: this.translate.instant('common.cancel'),
            variant: 'secondary',
            onClick: () => {
              this.editDialogVisible = false;
            },
          },
        ],
        component: {
          outlet: FormBuilderComponent,
          props: {
            formObject: this.editDialogFormObject,
          },
        },
      };
      this.editDialogVisible = true;
    });
  }

  performUpdateComs() {
    const selectedComs = this.userRepository.get<SelectOption[]>('selectedComs') || [];
    const comIds = selectedComs.map(com => Number(com.code));
    const userDto = this.userRepository.get('dto') as any;
    // Persist current roles as backend names, fallback to userDto.roles if selectedRoles is empty
    const selectedRoles = this.userRepository.get<SelectOption[]>('UserEnabledSelected');
    let roles: string[];
    if (selectedRoles && selectedRoles.length > 0) {
      roles = selectedRoles.map(role => String(role.code)).filter(code => !!code);
    } else if (Array.isArray(userDto.roles)) {
      roles = userDto.roles
        .map((r: string) => {
          const opt = this.rolesOptions.find(opt => opt.name === r || opt.label === r);
          return opt ? opt.name : r;
        })
        .filter(Boolean);
    } else {
      roles = this.userRoles;
    }
    // Persist current acs as ids, fallback to userDto.acIds if selectedAcs is empty
    const selectedAcs = this.userRepository.get<SelectOption[]>('selectedAcs');
    let acIds: number[];
    if (selectedAcs && selectedAcs.length > 0) {
      acIds = selectedAcs.map(ac => Number(ac.code));
    } else if (Array.isArray(userDto.acIds)) {
      acIds = [...userDto.acIds];
    } else {
      acIds = this.userAcs.value.map(ac => ac.id);
    }
    let acPiloteId = userDto.acPiloteId ? Number(userDto.acPiloteId) : 0;
    if ((!acPiloteId || acPiloteId === 0) && acIds.length > 0) {
      acPiloteId = acIds[0];
    }
    const requestBody: any = {
      username: userDto.username,
      firstName: userDto.firstName,
      lastName: userDto.lastName,
      email: userDto.email,
      address: userDto.address || '',
      mobile: userDto.mobile || '',
      fixe: userDto.fixe || '',
      password: userDto.password || '',
      comIds: comIds,
      acIds: acIds,
      acPiloteId: acPiloteId,
      roles: roles,
      enable: userDto.enable ?? true,
    };
    this.userService.updateUser(this.id, requestBody).subscribe({
      next: () => {
        const updatedUser = { ...userDto, comIds: comIds, acPiloteId };
        this.userRepository.set('dto', updatedUser);
        this.committeeService.getCommittees().subscribe(response => {
          this.userComs = response.content
            .filter(com => comIds.includes(com.id))
            .map(com => ({
              id: com.id,
              nom: com.nom,
              createdBy: com.metadata.createdBy,
              createdAt: com.metadata.createdAt,
            }));
        });
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.updated'),
        });
        this.loadUserData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.updated'),
        });
      },
    });
  }

  get userDto() {
    return this.userRepository.get<Partial<UserDto>>('dto');
  }

  onDetach(code: any, type: 'role' | 'ac' | 'com') {
    let itemName = '';
    let description = '';
    if (type === 'role') {
      const roleOption = this.rolesOptions.find(opt => opt.name === code);
      itemName = roleOption ? roleOption.label : code;
      description = this.translate.instant('settings.user.detach.confirmDescription', { item: itemName });
    } else if (type === 'ac') {
      const ac = this.userAcs.value.find(ac => ac.id === code);
      itemName = ac ? ac.nom : code;
      description = this.translate.instant('settings.user.detach.confirmDescriptionAcAndCom', { item: itemName });
    } else if (type === 'com') {
      const com = this.userComs.find(com => com.id === code);
      itemName = com ? com.nom : code;
      description = this.translate.instant('settings.user.detach.confirmDescriptionAcAndCom', { item: itemName });
    }

    this.userDialog = {
      title: this.translate.instant('settings.user.detach.title'),
      description: description,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onHide: () => {},
      actions: [
        {
          label: this.translate.instant('common.confirm'),
          variant: 'danger',
          onClick: () => {
            this.dialogVisible = false;
            this.performDetach(code, type);
          },
        },
        {
          label: this.translate.instant('common.cancel'),
          variant: 'secondary',
          onClick: () => {
            this.dialogVisible = false;
          },
        },
      ],
    };
    this.dialogVisible = true;
  }

  performDetach(code: any, type: 'role' | 'ac' | 'com') {
    const userDto = this.userRepository.get('dto') as any;

    // Always get the latest arrays
    let roles = Array.isArray(userDto.roles) ? [...userDto.roles] : [];
    let acIds = Array.isArray(userDto.acIds) ? [...userDto.acIds] : [];
    let comIds = Array.isArray(userDto.comIds) ? [...userDto.comIds] : [];
    let acPiloteId = userDto.acPiloteId;

    if (type === 'role') {
      // Remove by name only (code is always the role name)
      roles = roles.filter((r: string) => r !== code);
      // Ensure all roles are names (not labels)
      roles = roles
        .map((r: string) => {
          const opt = this.rolesOptions.find(opt => opt.name === r || opt.label === r);
          return opt ? opt.name : r;
        })
        .filter(Boolean);
    }

    if (type === 'ac') {
      acIds = acIds.filter((id: number) => id !== Number(code));
      // If acPiloteId is the one being removed, reset it
      acPiloteId = acIds.includes(acPiloteId) ? acPiloteId : 0;
      // Persist current roles as backend names
      roles = Array.isArray(userDto.roles)
        ? userDto.roles
            .map((r: string) => {
              const opt = this.rolesOptions.find(opt => opt.name === r || opt.label === r);
              return opt ? opt.name : r;
            })
            .filter(Boolean)
        : [];
      // Persist current coms as ids
      comIds = Array.isArray(userDto.comIds) ? [...userDto.comIds] : [];
    }

    if (type === 'com') {
      comIds = comIds.filter((id: number) => id !== Number(code));
      // Persist current roles as backend names
      roles = Array.isArray(userDto.roles)
        ? userDto.roles
            .map((r: string) => {
              const opt = this.rolesOptions.find(opt => opt.name === r || opt.label === r);
              return opt ? opt.name : r;
            })
            .filter(Boolean)
        : [];
      // Persist current acs as ids
      acIds = Array.isArray(userDto.acIds) ? [...userDto.acIds] : [];
    }

    // Validation: roles must not be empty and must all be valid names
    if (!roles.length || roles.some(r => !this.rolesOptions.find(opt => opt.name === r))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'La liste des rôles est invalide ou vide. Veuillez sélectionner au moins un rôle valide.',
      });
      return;
    }

    // Always send all arrays in the request
    const requestBody: Partial<UserStringDto> = {
      username: userDto.username,
      firstName: userDto.firstName,
      lastName: userDto.lastName,
      email: userDto.email,
      address: userDto.address || '',
      mobile: userDto.mobile || '',
      fixe: userDto.fixe || '',
      password: userDto.password || '',
      comIds: comIds,
      acIds: acIds,
      roles: roles,
      enable: userDto.enable ?? true,
    };
    if (typeof acPiloteId === 'number' && acPiloteId !== 0) {
      requestBody.acPiloteId = acPiloteId;
    }

    this.userService.updateUser(this.id, requestBody as UserStringDto).subscribe({
      next: () => {
        if (type === 'role') {
          this.userRoles = roles;
          this.updateRolesTableData();
          this.userRepository.set('dto', { ...userDto, roles });
        }
        if (type === 'ac') {
          this.userAcs.next(this.userAcs.value.filter(ac => ac.id !== Number(code)));
          this.userRepository.set('dto', { ...userDto, acIds, acPiloteId });
        }
        if (type === 'com') {
          this.userComs = this.userComs.filter(com => com.id !== Number(code));
          this.userRepository.set('dto', { ...userDto, comIds });
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.detached', { type }),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.detached', { type }),
        });
      },
    });
  }

  onAcPiloteChange(row: any, checked: boolean) {
    if (!checked) {
      // If unchecking, just update the AC Pilote
      this.updateAcPilote(null);
      return;
    }

    // Show confirmation dialog when setting a new AC Pilote
    this.userDialog = {
      title: this.translate.instant('settings.user.acPilote.confirmTitle'),
      description: this.translate.instant('settings.user.acPilote.confirmDescription', { ac: row.nom }),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onHide: () => {},
      actions: [
        {
          label: this.translate.instant('common.confirm'),
          variant: 'success',
          onClick: () => {
            this.dialogVisible = false;
            this.updateAcPilote(row.id);
          },
        },
        {
          label: this.translate.instant('common.cancel'),
          variant: 'secondary',
          onClick: () => {
            this.dialogVisible = false;
            // Reset the switch state
            this.userAcs.next(
              this.userAcs.value.map(ac => ({
                ...ac,
                isPilote: ac.id === this.userDto.acPiloteId,
              }))
            );
          },
        },
      ],
    };
    this.dialogVisible = true;
  }

  updateAcPilote(acPiloteId: number | null) {
    // Get the complete user object from state
    const userDto = this.userRepository.get('dto') as any;

    // Get all AC IDs from the current state
    const currentAcIds = this.userAcs.value.map(ac => ac.id);

    // Extract COM IDs from the response arrays
    const comIds = (userDto.coms || []).map((com: any) => com.id);

    // Ensure all roles are names (not labels)
    const roles = Array.isArray(userDto.roles)
      ? userDto.roles
          .map((r: string) => {
            const opt = this.rolesOptions.find(opt => opt.name === r || opt.label === r);
            return opt ? opt.name : r;
          })
          .filter(Boolean)
      : [];

    // Build request body, omitting acPiloteId if not a valid number
    const requestBody: Partial<UserStringDto> = {
      username: userDto.username,
      firstName: userDto.firstName,
      lastName: userDto.lastName,
      email: userDto.email,
      address: userDto.address,
      mobile: userDto.mobile,
      fixe: userDto.fixe,
      comIds: comIds,
      acIds: currentAcIds,
      roles: roles,
      enable: userDto.enable ?? true,
    };
    if (typeof acPiloteId === 'number' && acPiloteId !== 0) {
      requestBody.acPiloteId = acPiloteId;
    }

    this.userService.updateUser(this.id, requestBody as UserStringDto).subscribe({
      next: () => {
        // Update the state with the modified user object
        const updatedUser = { ...userDto, acPiloteId: acPiloteId || 0 };
        this.userRepository.set('dto', updatedUser);

        // Update the ACs list to reflect the new AC Pilote
        this.userAcs.next(
          this.userAcs.value.map(ac => ({
            ...ac,
            acPilote: ac.id === acPiloteId,
          }))
        );

        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.user.message.success.updated'),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.user.message.error.updated'),
        });
        // Reset the switch state on error
        this.userAcs.next(
          this.userAcs.value.map(ac => ({
            ...ac,
            acPilote: ac.id === this.userDto.acPiloteId,
          }))
        );
      },
    });
  }

  resetPassword() {
    const id = this.userRepository.get<UserString>('response')?.id;
    this.userService.resetPassword(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          detail: this.translate.instant('settings.user.password.success'),
        });
        this.loading = false;
      },
      error: error => {
        const errorMessage = error.error?.message || error.message || 'Unknown error';
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('settings.user.password.error'),
          detail: errorMessage,
        });
        this.loading = false;
      },
    });
  }

  checkFormChanges() {
    const currentValues = this.userRepository.get('dto');
    this.hasFormChanges = JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);
  }

  cancelUserEdit() {
    this.checkFormChanges();
    if (this.hasFormChanges) {
      this.userDialog = {
        title: this.translate.instant('settings.user.update.confirmationDialog.title'),
        description: this.translate.instant('settings.user.update.confirmationDialog.description'),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onHide: () => {},
        actions: [
          {
            label: this.translate.instant('settings.user.update.confirmationDialog.yes'),
            variant: 'success',
            onClick: () => {
              this.dialogVisible = false;
              this.router.navigate(['/settings', 'user']);
            },
          },
          {
            label: this.translate.instant('settings.user.update.confirmationDialog.no'),
            variant: 'secondary',
            onClick: () => {
              this.dialogVisible = false;
            },
          },
        ],
      };
      this.dialogVisible = true;
    } else {
      this.router.navigate(['/settings', 'user']);
    }
  }
}
